import { FC, useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { useQuery } from '@tanstack/react-query';
import { defineMessages, useIntl } from 'react-intl';
import { MeApi } from 'src/api';
import { InfoTooltipModal, Page } from 'src/components';
import { ProgressPreferences, SummaryItem } from 'src/api/learningSessions/types';
import { DailyNewWordsResponse } from 'src/api/me/types';
import { UserPathBlob } from 'src/api/userPath/types';
import { DashboardHeader } from 'src/apps/DashboardFrame/DashboardHeader';
import { PathBeforeNavigate } from 'src/apps/Path/PathContinueButtons';
import { PathSessionType } from 'src/apps/Path/types';
import soundFile from 'src/apps/LearningSession/EndOfSession/SessionCompletedCelebration/points-update.mp3';
import { ONE_MINUTE } from 'src/constants/times.const';
import { useEligibility } from 'src/hooks/useEligibility';
import { getLanguagePair } from 'src/utils/language/getLanguagePair';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { localizeCelebrationTranslation } from 'src/apps/QuickReview/celebrationVideos';
import { useEosCelebrationVideo } from '../EndOfSessionLearn/useEosCelebrationVideo';
import { Actions } from './Actions';
import { CelebrationClip } from './CelebrationClip';
import { CompletedWordlist } from './CompletedWordlist';
import { DailyGoalBanner } from './DailyGoalBanner';
import { GrowthLegend } from './GrowthLegend';
import { groupSummaryItems } from './groupSummaryItems';
import { WordStatus } from './GrowthFlower';
import { pickSubheader } from './subheaders';
import { useEndOfSessionTracking } from './tracking';
import { useReachedDailyGoal } from './useReachedDailyGoal';
import { LoadingSkeleton } from './LoadingSkeleton';
import { WordGroup } from './WordGroup';
import * as S from './styles';

const msgs = defineMessages({
  pageTitle: { id: 'scenario_end_of_session_title' },
  fallbackTitle: { id: 'blob_eos_fallback_title', defaultMessage: 'Well done!' },
});

const BLOB_EOS_SCREEN = 'end_of_session';

const GROUP_ORDER: WordStatus[] = ['stillLearning', 'justLearned', 'markedAsKnown', 'reviewed'];

export type BlobEndOfSessionProps = {
  summaryItems: SummaryItem[];
  completedWordlists?: UserPathBlob[];
  progressPreferences: { [learnableId: number]: ProgressPreferences };
  isFirstSession: boolean;
  pathwayNodeId: number | null;
  pathSessionType: PathSessionType | null;
  continueReviewUrl: string | null;
  dailyNewWordsBefore?: DailyNewWordsResponse;
  sessionId: string;
  /** Canonical session type, as sent with the learning session events */
  sessionType: string;
  onBeforeExit: PathBeforeNavigate;
};

export const BlobEndOfSession: FC<BlobEndOfSessionProps> = ({
  summaryItems,
  completedWordlists = [],
  progressPreferences,
  isFirstSession,
  pathwayNodeId,
  pathSessionType,
  continueReviewUrl,
  dailyNewWordsBefore,
  sessionId,
  sessionType,
  onBeforeExit,
}) => {
  const intl = useIntl();
  const languagePairId = getLanguagePairId();
  const { eligibility } = useEligibility(languagePairId);
  const { data: userLanguages, isLoading: isUserLanguagesLoading } = useQuery({
    queryKey: ['userLanguages'],
    queryFn: () => MeApi.getUserLanguages(),
    staleTime: ONE_MINUTE,
    gcTime: ONE_MINUTE,
  });
  const video = useEosCelebrationVideo();
  const [subheaderSeed] = useState(Math.random);
  const subheader = pickSubheader(isFirstSession, () => subheaderSeed);
  const groups = groupSummaryItems(summaryItems, progressPreferences);
  const { hasReachedDailyGoal, isLoading: isDailyGoalLoading } = useReachedDailyGoal(
    languagePairId,
    dailyNewWordsBefore,
  );

  const track = useEndOfSessionTracking({
    languagePairId,
    sessionId,
    sessionType,
    hasReachedDailyGoal,
  });

  const hasTrackedScreenView = useRef(false);
  useEffect(() => {
    if (isDailyGoalLoading || hasTrackedScreenView.current) return;
    hasTrackedScreenView.current = true;
    track('screen_viewed', {
      still_learning_count: String(groups.stillLearning.length),
      marked_as_known_count: String(groups.markedAsKnown.length),
      just_learned_count: String(groups.justLearned.length),
      reviewed_count: String(groups.reviewed.length),
      is_first_session: String(isFirstSession),
      subheader: String(subheader.id),
      has_clip: String(!!video),
    });
  }, [isDailyGoalLoading, track, groups, isFirstSession, subheader, video]);

  if (isDailyGoalLoading) return <LoadingSkeleton />;

  return (
    <Page footerStyle="none">
      <Head>
        <title>{intl.formatMessage(msgs.pageTitle)} - Memrise</title>
      </Head>
      <S.Screen data-testid="blob-end-of-session">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio autoPlay preload="auto" src={soundFile} data-testid="session-completed-sound" />
        <S.HeaderSlot>
          <DashboardHeader
            languagePairId={languagePairId ?? undefined}
            languagePair={getLanguagePair(userLanguages, languagePairId ?? undefined)}
            isLanguagePairLoading={isUserLanguagesLoading}
            isBlobExperience
            isSupportedLanguagePairBuddies={eligibility?.eligibleForAiBuddies}
            showLogo
            screen={BLOB_EOS_SCREEN}
          />
        </S.HeaderSlot>
        <S.ScrollArea
          data-testid="blob-eos-scroll-area"
          role="region"
          aria-label={intl.formatMessage(msgs.pageTitle)}
          tabIndex={0}
        >
          <S.Content>
            <S.Hero>
              {video && (
                <CelebrationClip
                  src={video.src}
                  onAutoplay={result => track('celebration_clip_autoplay', { result })}
                  onPlay={trigger => track('celebration_clip_played', { trigger })}
                />
              )}
              <S.HeroText>
                <S.TitleRow>
                  <S.Title dir="auto">
                    {video?.phrase ?? intl.formatMessage(msgs.fallbackTitle)}
                  </S.Title>
                  {video?.translation && (
                    <S.TranslationHint>
                      <InfoTooltipModal
                        tooltip={
                          <span dir="auto">
                            {localizeCelebrationTranslation(video.translation, intl.locale)}
                          </span>
                        }
                      />
                    </S.TranslationHint>
                  )}
                </S.TitleRow>
                <S.Subheader>{intl.formatMessage(subheader)}</S.Subheader>
              </S.HeroText>
            </S.Hero>
            <S.Results data-testid="blob-eos-results">
              {completedWordlists.map(wordlist => (
                <CompletedWordlist key={wordlist.blobId} wordlist={wordlist} />
              ))}
              {hasReachedDailyGoal && <DailyGoalBanner />}
              <S.Groups>
                {GROUP_ORDER.filter(status => groups[status].length > 0).map(status => (
                  <WordGroup key={status} status={status} items={groups[status]} />
                ))}
                {isFirstSession && <GrowthLegend />}
              </S.Groups>
            </S.Results>
          </S.Content>
        </S.ScrollArea>
        <S.Footer data-testid="blob-eos-footer">
          <Actions
            languagePairId={languagePairId}
            pathwayNodeId={pathwayNodeId}
            pathSessionType={pathSessionType}
            continueReviewUrl={continueReviewUrl}
            hasReachedDailyGoal={hasReachedDailyGoal}
            onBeforeExit={onBeforeExit}
            track={track}
          />
        </S.Footer>
      </S.Screen>
    </Page>
  );
};
